import { InfiniteData, infiniteQueryOptions, QueryClient, queryOptions } from '@tanstack/react-query';
import { produce } from 'immer';
import _ from 'lodash-es';

import { buildURL, get, post, put } from '@/shared/fetch';

import { getPlaylistQueryOption } from '../playlists';
import { Playlist } from '../playlists/types';
import { CursorPagination, Pagination, PaginationQueryParams } from '../types';
import { getInfiniteQueryFn, getQueryFn } from '../utils';
import {
  AdminUploadMediaRequest,
  AdminUploadMediaResponse,
  GetMediaResponse,
  Media,
  MediaType,
  Photo,
  SnapshotPhoto,
  UpdateSnapshotPhotoRequest,
  UpdateSnapshotPhotoResponse,
  Video,
  VideoType,
} from './types';

export interface InfiniteVideosQuerySearchParams extends PaginationQueryParams {
  type?: string;
  habitatIds?: string;
  search?: string;
  highlight?: string;
  isEpisodic?: '0' | '1';
}

export interface InfiniteMediaQuerySearchParams extends PaginationQueryParams {
  mediaType?: MediaType;
  videoType?: VideoType | string;
  habitatId?: string[] | string;
  zooId?: string[] | string;
  search?: string;
  highlight?: string;
  isEpisodic?: '0' | '1';
  isViewed?: '0' | '1';
  startDate?: string;
  endDate?: string;
  likedBy?: string;
}

export const getSnapshotPhotoQueryOptions = (snapshotId: string) =>
  queryOptions({
    queryKey: ['snapshot', snapshotId],
    queryFn: getQueryFn<SnapshotPhoto>(`/photos/${snapshotId}`),
    select: ({ rawURL }) => rawURL,
    enabled: Boolean(snapshotId),
  });

export const getInfiniteVideosQueryOptions = (searchParams?: InfiniteVideosQuerySearchParams) => {
  const { pageSize = 12 } = searchParams ?? {};
  return infiniteQueryOptions({
    queryKey: ['videos', 'list', searchParams],
    initialPageParam: 1,
    queryFn: getInfiniteQueryFn<Pagination<Video>>('/videos', searchParams),
    getNextPageParam: (lastPage, _, lastPageParams) =>
      !lastPage ? 1 : lastPage.total > pageSize * lastPageParams ? lastPageParams + 1 : undefined,
    select: ({ pages }) =>
      pages.length > 0
        ? ({ total: pages[pages.length - 1].total, list: pages.flatMap((page) => page.list) } as Pagination<Video>)
        : undefined,
  });
};

export const getInfiniteMediaQueryOptions = (searchParams?: InfiniteMediaQuerySearchParams) => {
  const { habitatId, zooId, pageSize = 12, ...rest } = searchParams ?? {};
  const params = {
    ...rest,
    pageSize,
    habitatId: Array.isArray(habitatId) ? habitatId.join(',') : habitatId,
    zooId: Array.isArray(zooId) ? zooId.join(',') : zooId,
  };

  return infiniteQueryOptions({
    queryKey: ['media', 'list', 'page', params],
    initialPageParam: 1,
    queryFn: getInfiniteQueryFn<Pagination<Media>>('/media', params),
    getNextPageParam: (lastPage, _, lastPageParams) =>
      !lastPage ? 1 : lastPage.total > pageSize * lastPageParams ? lastPageParams + 1 : undefined,
    select: ({ pages }) =>
      pages.length > 0
        ? ({ total: pages[pages.length - 1].total, list: pages.flatMap((page) => page.list) } as Pagination<Media>)
        : undefined,
  });
};

export const getInfiniteCursorBasedMediaQueryOptions = (searchParams?: InfiniteMediaQuerySearchParams) => {
  const { habitatId, zooId, ...rest } = searchParams ?? {};
  const params = {
    pageSize: 12,
    ...rest,
    habitatId: Array.isArray(habitatId) ? habitatId.join(',') : habitatId,
    zooId: Array.isArray(zooId) ? zooId.join(',') : zooId,
  };

  return infiniteQueryOptions({
    queryKey: ['media', 'list', 'cursor', params],
    initialPageParam: '',
    queryFn: getInfiniteQueryFn<CursorPagination<Media>>('/media', params),
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    select: ({ pages }) =>
      pages.length > 0
        ? ({
            list: pages.flatMap((page) => page.list),
            nextCursor: pages[pages.length - 1].nextCursor,
          } as CursorPagination<Media>)
        : undefined,
  });
};

export const getMediaQueryOptions = (mediaId: string | undefined | null) =>
  queryOptions({
    enabled: Boolean(mediaId),
    queryKey: ['media', 'detail', mediaId],
    queryFn: getQueryFn<GetMediaResponse>(`/media/${mediaId}`),
    select: ({ media }) => media,
  });

export const getMedia = (mediaId: string) => get<GetMediaResponse>(buildURL(`/media/${mediaId}`)).then(({ media }) => media);

export const adminUploadMediaMutationFn = (args: AdminUploadMediaRequest) =>
  post<AdminUploadMediaResponse>(buildURL('/admin/media'), args);

export const updateVideoMutationFn = async (
  args: Omit<Partial<Video>, 'id'> & { overlayThumbnail?: boolean; playlists?: string[] },
) => put<void>(buildURL(`/videos/${args._id}`), args);

export const updatePhotoMutationFn = async (args: Omit<Partial<Photo>, 'id'>) =>
  put<void>(buildURL(`/photos/${args._id}`), args);

export const updateMediaInQueryClient = (
  queryClient: QueryClient,
  mediaId: string,
  update: Partial<Media>,
  mediaType?: MediaType,
) => {
  // ignore `type` field from being updated as it is not the same value from list and detail
  // and usually it is not needed to update
  const updateWithoutType = _.omit(update, 'type');
  // update media detail
  queryClient.setQueryData(getMediaQueryOptions(mediaId).queryKey, (data) =>
    data ? ({ ...data, media: { ...data.media, ...updateWithoutType } } as GetMediaResponse) : data,
  );
  // update media list
  queryClient.setQueriesData(
    { queryKey: getInfiniteMediaQueryOptions().queryKey.slice(0, 2) },
    produce<InfiniteData<Pagination<Media>> | undefined>((data) => {
      data?.pages?.forEach((page) => {
        // eslint-disable-next-line no-param-reassign
        page.list = page.list.map((item) => {
          if (item._id === mediaId) {
            return { ...item, ...updateWithoutType } as Media;
          }
          return item;
        });
      });
    }),
  );
  // skip update video related queries if mediaType is specified as photo
  if (mediaType === 'photo') {
    return;
  }
  // update videos list
  queryClient.setQueriesData(
    { queryKey: getInfiniteVideosQueryOptions().queryKey.slice(0, 2) },
    produce<InfiniteData<Pagination<Video>> | undefined>((data) => {
      data?.pages?.forEach((page) => {
        // eslint-disable-next-line no-param-reassign
        page.list = page.list.map((item) => {
          if (item._id === mediaId) {
            // ignore `type` field from being updated as it is not the same value from list and detail
            // and usually it is not needed to update
            return { ...item, ...updateWithoutType } as Video;
          }
          return item;
        });
      });
    }),
  );
  // update playlists detail
  queryClient.setQueriesData(
    { queryKey: getPlaylistQueryOption('').queryKey.slice(0, 2) },
    produce<Playlist | undefined>((data) => {
      if (!data) return;
      // eslint-disable-next-line no-param-reassign
      data.videos = data.videos.map((item) => {
        if (item._id === mediaId) {
          // ignore `type` field from being updated as it is not the same value from list and detail
          // and usually it is not needed to update
          return { ...item, ...updateWithoutType } as Video;
        }
        return item;
      });
    }),
  );
};

export const updateSnapshotPhotoMutationFn = async ({ snapshotId, ...args }: UpdateSnapshotPhotoRequest) =>
  put<UpdateSnapshotPhotoResponse>(buildURL(`/photos/${snapshotId}`), args);
